/**
 * BHADKO OTT - Admin Management Console Controller
 * CRUD operations, channel validation, logo customization, 
 * stream preview, reordering, and catalog synchronization.
 */

import { StorageManager } from './storage.js';
import { ICONS, getChannelLogoSvg } from './assets.js';

export class AdminController {
  constructor(options = {}) {
    this.container = options.container;
    this.onCatalogUpdated = options.onCatalogUpdated || (() => {});
    this.onPreviewStream = options.onPreviewStream || (() => {});
    this.currentEditingId = null;
    this.channelToDeleteId = null;
  }

  renderAdminView(lang, translations) {
    const channels = StorageManager.getChannels();
    const t = translations[lang] || translations.gu;

    return `
      <div class="admin-dashboard-wrapper">
        <div class="admin-header-bar">
          <div class="admin-header-text">
            <h2>${t.adminTitle}</h2>
            <p>${t.adminSubtitle}</p>
          </div>
          <div class="admin-actions-group">
            <button class="btn btn-primary" id="btnAdminAddChannel">
              ${t.btnAddNewChannel}
            </button>
          </div>
        </div>

        <div class="admin-table-container">
          <table class="admin-channels-table">
            <thead>
              <tr>
                <th>${t.colLogo}</th>
                <th>${t.colName}</th>
                <th>${t.colCategory}</th>
                <th>${t.colLanguage}</th>
                <th>${t.colStatus}</th>
                <th>${t.colStream}</th>
                <th style="text-align: right;">${t.colActions}</th>
              </tr>
            </thead>
            <tbody>
              ${channels.map(channel => this.renderTableRow(channel, t)).join('')}
            </tbody>
          </table>
        </div>
      </div>
    `;
  }

  renderTableRow(channel, t) {
    const isLive = channel.status === 'LIVE';
    const isStartingSoon = channel.status === 'STARTING_SOON';
    
    let statusBadge = `<span class="status-pill status-offline">${t.offline}</span>`;
    if (isLive) {
      statusBadge = `<span class="status-pill status-live"><span class="pulse-dot"></span> ${t.liveBadge}</span>`;
    } else if (isStartingSoon) {
      statusBadge = `<span class="status-pill status-soon">${t.startingSoon}</span>`;
    }

    return `
      <tr data-channel-id="${channel.id}">
        <td class="col-logo-cell">
          <div class="table-logo-preview">
            ${getChannelLogoSvg(channel)}
          </div>
        </td>
        <td class="col-name-cell">
          <div class="table-channel-title">
            <strong>${channel.name}</strong>
            <span class="gujarati-sub">${channel.gujaratiName || ''}</span>
          </div>
        </td>
        <td><span class="category-badge">${channel.category}</span></td>
        <td>${channel.language || 'Gujarati'}</td>
        <td>
          <button class="toggle-status-btn" data-action="toggle-status" data-id="${channel.id}" title="Toggle Live/Offline">
            ${statusBadge}
          </button>
        </td>
        <td class="col-stream-cell">
          <span class="stream-url-snippet" title="${channel.streamUrl}">
            ${channel.streamUrl ? channel.streamUrl.substring(0, 32) + '...' : 'No stream'}
          </span>
        </td>
        <td class="col-actions-cell" style="text-align: right;">
          <div class="table-actions">
            <button class="btn-icon" data-action="preview" data-id="${channel.id}" title="${t.btnPreview}">
              ${ICONS.eye}
            </button>
            <button class="btn-icon" data-action="edit" data-id="${channel.id}" title="${t.btnEdit}">
              ${ICONS.edit}
            </button>
            <button class="btn-icon btn-danger" data-action="delete" data-id="${channel.id}" title="${t.btnDelete}">
              ${ICONS.trash}
            </button>
          </div>
        </td>
      </tr>
    `;
  }

  renderEditModal(channel = null, lang, translations) {
    const t = translations[lang] || translations.gu;
    const isEdit = !!channel;
    const title = isEdit ? t.modalEditTitle : t.modalAddTitle;

    const currentChannel = channel || {
      id: '',
      name: '',
      gujaratiName: '',
      category: 'News',
      language: 'Gujarati',
      status: 'LIVE',
      isFeatured: false,
      isVerified: true,
      displayOrder: 1,
      streamUrl: 'https://test-streams.mux.dev/x36xhzz/x36xhzz.m3u8',
      description: '',
      logoColor: '#FF6B00'
    };

    return `
      <div class="modal-backdrop" id="channelEditorModal">
        <div class="modal-card">
          <div class="modal-header">
            <h3>${title}</h3>
            <button class="modal-close-btn" id="modalCloseBtn">${ICONS.close}</button>
          </div>
          <form id="channelForm" class="modal-body-form">
            <input type="hidden" id="editChannelId" value="${currentChannel.id}" />

            <div class="form-row two-cols">
              <div class="form-group">
                <label for="inputChannelName">${t.fieldChannelName} *</label>
                <input type="text" id="inputChannelName" required value="${currentChannel.name}" placeholder="e.g. Bhadko TV" />
              </div>
              <div class="form-group">
                <label for="inputGujaratiName">${t.fieldGujaratiName} *</label>
                <input type="text" id="inputGujaratiName" required value="${currentChannel.gujaratiName}" placeholder="દા.ત. ગુજરાતી સમાચાર" />
              </div>
            </div>

            <div class="form-row three-cols">
              <div class="form-group">
                <label for="inputCategory">${t.fieldCategory}</label>
                <select id="inputCategory">
                  <option value="News" ${currentChannel.category === 'News' ? 'selected' : ''}>News (સમાચાર)</option>
                  <option value="Sports" ${currentChannel.category === 'Sports' ? 'selected' : ''}>Sports (રમત ગમત)</option>
                  <option value="Entertainment" ${currentChannel.category === 'Entertainment' ? 'selected' : ''}>Entertainment (મનોરંજન)</option>
                  <option value="Devotional" ${currentChannel.category === 'Devotional' ? 'selected' : ''}>Devotional (ભક્તિ)</option>
                  <option value="Music" ${currentChannel.category === 'Music' ? 'selected' : ''}>Music (સંગીત)</option>
                  <option value="Education" ${currentChannel.category === 'Education' ? 'selected' : ''}>Education (શિક્ષણ)</option>
                  <option value="Movies" ${currentChannel.category === 'Movies' ? 'selected' : ''}>Movies (ચલચિત્ર)</option>
                </select>
              </div>

              <div class="form-group">
                <label for="inputLanguage">${t.fieldLanguage}</label>
                <select id="inputLanguage">
                  <option value="Gujarati" ${currentChannel.language === 'Gujarati' ? 'selected' : ''}>Gujarati (ગુજરાતી)</option>
                  <option value="Hindi" ${currentChannel.language === 'Hindi' ? 'selected' : ''}>Hindi (हिन्दी)</option>
                  <option value="English" ${currentChannel.language === 'English' ? 'selected' : ''}>English</option>
                </select>
              </div>

              <div class="form-group">
                <label for="inputStatus">${t.fieldStatus}</label>
                <select id="inputStatus">
                  <option value="LIVE" ${currentChannel.status === 'LIVE' ? 'selected' : ''}>LIVE (સક્રિય પ્રસારણ)</option>
                  <option value="STARTING_SOON" ${currentChannel.status === 'STARTING_SOON' ? 'selected' : ''}>STARTING SOON (ટૂંક સમયમાં)</option>
                  <option value="OFFLINE" ${currentChannel.status === 'OFFLINE' ? 'selected' : ''}>OFFLINE (ઓફલાઇન)</option>
                </select>
              </div>
            </div>

            <div class="form-group">
              <label for="inputStreamUrl">${t.fieldStreamUrl} *</label>
              <input type="url" id="inputStreamUrl" required value="${currentChannel.streamUrl}" placeholder="https://example.com/stream/index.m3u8" />
              <small class="form-hint">HLS live or VOD master manifest URL. HTTPS required.</small>
            </div>

            <div class="form-group">
              <label for="inputDescription">${t.fieldDescription}</label>
              <textarea id="inputDescription" rows="2" placeholder="ચેનલની ટૂંકી માહિતી...">${currentChannel.description || ''}</textarea>
            </div>

            <div class="form-row two-cols">
              <div class="form-group">
                <label for="inputLogoColor">Branding Color</label>
                <input type="color" id="inputLogoColor" value="${currentChannel.logoColor || '#FF6B00'}" class="color-picker-input" />
              </div>
              <div class="form-group">
                <label for="inputDisplayOrder">${t.fieldOrder}</label>
                <input type="number" id="inputDisplayOrder" min="1" value="${currentChannel.displayOrder || 1}" />
              </div>
            </div>

            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" id="btnCancelEdit">${t.btnCancel}</button>
              <button type="submit" class="btn btn-primary" id="btnSaveChannel">${t.btnSave}</button>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  renderDeleteConfirmModal(channel, lang, translations) {
    const t = translations[lang] || translations.gu;

    return `
      <div class="modal-backdrop" id="deleteConfirmModal">
        <div class="modal-card modal-confirm">
          <div class="modal-header">
            <h3>${t.confirmDeleteTitle}</h3>
            <button class="modal-close-btn" id="modalCloseDeleteBtn">${ICONS.close}</button>
          </div>
          <div class="modal-body">
            <p>${t.confirmDeleteDesc}</p>
            <div class="confirm-channel-preview">
              ${getChannelLogoSvg(channel)}
              <div>
                <strong>${channel.name}</strong>
                <span>${channel.gujaratiName || ''}</span>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" id="btnCancelDelete">${t.btnCancel}</button>
            <button type="button" class="btn btn-danger" id="btnConfirmDeleteFinal">${t.btnDelete}</button>
          </div>
        </div>
      </div>
    `;
  }
}
